import React, { useRef, useState } from 'react';
import { ZoomIn } from 'lucide-react';
import { useLightbox } from '../../utils/hooks/useLightbox';
import { useLocalization } from '../../utils/hooks/useLocalization';
import styles from './ProductGallery.module.css';

/**
 * Inline product gallery. One track for both breakpoints: a 2x2 grid on desktop,
 * a scroll-snap strip with dots below 768px. CSS decides, so there is no JS
 * layout branch.
 */
const ProductGallery = ({ images = [], name }) => {
  const { t } = useLocalization();
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);
  const { triggerProps, lightbox } = useLightbox({
    images,
    name,
    label: (i) => `${name} ${i + 1} - ${t('product_detail_gallery_zoom')}`,
  });

  if (!images.length) return null;

  return (
    <div className={styles.gallery}>
      <div
        ref={trackRef}
        className={styles.track}
        onScroll={() => {
          const el = trackRef.current;
          if (el && el.clientWidth) setActive(Math.round(el.scrollLeft / el.clientWidth));
        }}
      >
        {images.map((src, index) => (
          <a key={src} {...triggerProps(index)} className={styles.cell}>
            <img
              src={src}
              alt={`${name} ${index + 1}`}
              className={styles.image}
              loading={index === 0 ? 'eager' : 'lazy'}
              decoding="async"
            />
            <span className={styles.zoomOverlay} aria-hidden="true">
              <ZoomIn size={28} />
            </span>
          </a>
        ))}
      </div>

      {images.length > 1 && (
        <div className={styles.dots}>
          {images.map((src, i) => (
            <button
              key={`dot-${src}`}
              type="button"
              className={i === active ? `${styles.dot} ${styles.dotActive}` : styles.dot}
              onClick={() => {
                const el = trackRef.current;
                if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
              }}
              aria-label={`${name} ${i + 1}`}
              aria-current={i === active}
            />
          ))}
        </div>
      )}

      {lightbox}
    </div>
  );
};

export default ProductGallery;
